const parseDateMs = value => {
	if (!value) return null
	const ms = new Date(value).getTime()
	return Number.isFinite(ms) ? ms : null
}

const formatDate = value => {
	const ms = parseDateMs(value)
	if (!Number.isFinite(ms)) return null
	return new Date(ms).toLocaleDateString('en-US', {
		month: 'short',
		day: 'numeric',
		year: 'numeric',
	})
}

const getGradeKeyFromStudentGrade = grade => {
	if (grade === undefined || grade === null) return null
	const str = String(grade).trim()
	if (!str) return null
	const match = str.match(/\d+/)
	if (!match) return null
	const n = parseInt(match[0], 10)
	if (!Number.isFinite(n) || n < 1 || n > 20) return null
	return String(n)
}

const formatAssignmentGradeLabel = gradeKeys => {
	if (!gradeKeys || gradeKeys.length === 0) return null
	const sorted = [...new Set(gradeKeys)]
		.map(g => parseInt(g, 10))
		.filter(n => Number.isFinite(n))
		.sort((a, b) => a - b)
	if (sorted.length === 0) return null
	return `Grade: ${sorted.join(', ')}`
}

export const aggregateAssignmentTasks = (tasks, studentGradeById = null) => {
	const list = Array.isArray(tasks) ? tasks : []
	const map = new Map()

	list.forEach(task => {
		const taskId = String(task?.task_id || '').trim()
		const studentId = String(task?.studentId || '').trim()
		if (!taskId || !studentId) return

		if (!map.has(taskId)) {
			const isDocumentType =
				String(task?.type || '').toLowerCase() === 'document'
			const name =
				isDocumentType && (!task?.task || task.task === 'Form Completion')
					? 'Documents'
					: task?.task || 'Untitled Assignment'
			map.set(taskId, {
				id: taskId,
				name,
				type: task?.type || 'General',
				status: 'active',
				studentsCount: 0,
				completion: 0,
				_completedCount: 0,
				lastUpdated: null,
				lastUpdatedAt: null,
				_lastUpdatedTs: null,
				created: formatDate(task?.created_at),
				createdAt: task?.created_at || null,
				meta: '',
				newStudentsCount: 0,
				assignmentData: {
					title: name,
					description: task?.task_description || '',
					custom_message: task?.custom_message || '',
					due_date: task?.dueDate || task?.due_date,
					start_date: task?.start_date,
					end_date: task?.end_date,
					type: task?.type || 'General',
					assignment_url: task?.assignment_url,
				},
				students: [],
			})
		}

		const assignment = map.get(taskId)
		const grade =
			task?.grade || studentGradeById?.get?.(String(studentId).trim()) || null
		const normalizedStatus =
			task?.student_status === 'completed'
				? 'completed'
				: task?.student_status === 'in_progress'
					? 'in_progress'
					: 'not_started'
		const isNew = Boolean(task?.is_new)
		assignment.students.push({
			id: studentId,
			name: task?.studentName || 'Unknown Student',
			country: task?.studentCountry,
			grade,
			status: normalizedStatus,
			response_text: task?.response_text,
			submission_date: task?.submission_date,
			completed_at: task?.completed_at,
			taskId,
			isNew,
		})
		assignment.studentsCount += 1
		if (normalizedStatus === 'completed') assignment._completedCount += 1
		if (isNew) assignment.newStudentsCount += 1

		const updatedTs = Math.max(
			parseDateMs(task?.completed_at) || 0,
			parseDateMs(task?.submission_date) || 0,
		)
		if (updatedTs && (!assignment._lastUpdatedTs || updatedTs > assignment._lastUpdatedTs)) {
			assignment._lastUpdatedTs = updatedTs
		}
	})

	const assignments = Array.from(map.values())
	assignments.forEach(assignment => {
		assignment.completion =
			assignment.studentsCount > 0
				? Math.round((assignment._completedCount / assignment.studentsCount) * 100)
				: 0
		assignment.status =
			assignment.completion === 100
				? 'completed'
				: assignment.completion > 0
					? 'in_progress'
					: 'active'
		if (assignment._lastUpdatedTs) {
			const d = new Date(assignment._lastUpdatedTs)
			assignment.lastUpdatedAt = d.toISOString()
			assignment.lastUpdated = d.toLocaleDateString('en-US', {
				month: 'short',
				day: 'numeric',
				year: 'numeric',
			})
		}
		const gradeKeys = (assignment.students || [])
			.map(student => getGradeKeyFromStudentGrade(student?.grade))
			.filter(Boolean)
		assignment.gradeKeys = [...new Set(gradeKeys)]
		assignment.gradeLabel = formatAssignmentGradeLabel(gradeKeys)
		delete assignment._completedCount
		delete assignment._lastUpdatedTs
	})

	return assignments
}

export const filterAssignmentsLocal = (
	assignments,
	{ gradeTab = 'all', activeFilterIds = ['all'] } = {},
) => {
	let list = Array.isArray(assignments) ? [...assignments] : []

	if (gradeTab !== 'all') {
		const target = String(gradeTab).replace('grade_', '')
		list = list.filter(assignment =>
			(assignment?.students || []).some(
				student => getGradeKeyFromStudentGrade(student?.grade) === target,
			),
		)
	}

	const active = (activeFilterIds || []).filter(id => id !== 'all')
	if (active.length) {
		const set = new Set(active)
		list = list.filter(assignment => {
			if (set.has('active') && String(assignment.status) === 'active') return true
			if (set.has('completed') && String(assignment.status) === 'completed') {
				return true
			}
			if (set.has('low')) {
				const completion = Number(assignment.completion)
				return (
					!Number.isNaN(completion) &&
					completion < 60 &&
					String(assignment.status) === 'active'
				)
			}
			if (set.has('new') && (Number(assignment.newStudentsCount) || 0) > 0) {
				return true
			}
			return false
		})
	}

	return list
}

export const sortAssignmentsLocal = (assignments, sortKey, sortDir = 'asc') => {
	if (!sortKey) return Array.isArray(assignments) ? [...assignments] : []
	const dir = sortDir === 'desc' ? -1 : 1
	const getValue = assignment => {
		if (sortKey === 'name') return assignment.name
		if (sortKey === 'status') return assignment.status
		if (sortKey === 'students') return assignment.studentsCount
		if (sortKey === 'completion') return assignment.completion
		if (sortKey === 'created') {
			return assignment.createdAt ? new Date(assignment.createdAt).getTime() : null
		}
		if (sortKey === 'lastUpdated') {
			return assignment.lastUpdatedAt
				? new Date(assignment.lastUpdatedAt).getTime()
				: null
		}
		return assignment[sortKey]
	}

	return [...assignments].sort((a, b) => {
		const av = getValue(a)
		const bv = getValue(b)
		if (av === null && bv === null) return 0
		if (av === null) return 1
		if (bv === null) return -1
		if (typeof av === 'number' && typeof bv === 'number') {
			return (av - bv) * dir
		}
		return (
			String(av).localeCompare(String(bv), undefined, {
				sensitivity: 'base',
			}) * dir
		)
	})
}

export const gradeAssignmentCounts = assignments => {
	const counts = {
		all: assignments.length,
		grade_9: 0,
		grade_10: 0,
		grade_11: 0,
		grade_12: 0,
	}
	assignments.forEach(assignment => {
		const gradeKeys = new Set(assignment?.gradeKeys || [])
		if (gradeKeys.has('9')) counts.grade_9 += 1
		if (gradeKeys.has('10')) counts.grade_10 += 1
		if (gradeKeys.has('11')) counts.grade_11 += 1
		if (gradeKeys.has('12')) counts.grade_12 += 1
	})
	return counts
}
